// utils/poolaReportFormat.js
//
// Pure, presentation-only formatting helpers for the supplier "final report"
// (post-mortem) screen. No i18n, no reactivity — components call these at
// render time and wrap the result in i18n interpolation. Money stays in
// integer EGP cents everywhere upstream (composable + service); these helpers
// are the ONLY place cents get divided down to whole EGP units for display,
// matching the accepted design (report numbers are whole units, no decimals).
//
// Digits are ALWAYS Western (owner-locked UI rule) — Number.toLocaleString('en-US')
// and Intl.DateTimeFormat(..., '-u-nu-latn') both guarantee this regardless of
// the active app locale.

/** Round a fraction (0..1) to a clamped whole percentage string, e.g. "71%". */
export function formatPercent(fraction) {
  const n = Number(fraction);
  const pct = Number.isFinite(n) ? Math.round(n * 100) : 0;
  return `${Math.max(0, Math.min(100, pct))}%`;
}

/** Grouped whole-number string with Western digits, e.g. 1240 -> "1,240". */
export function formatWholeNumber(value) {
  const n = Number(value);
  const rounded = Number.isFinite(n) ? Math.round(n) : 0;
  return rounded.toLocaleString('en-US');
}

/** Integer EGP cents -> grouped whole-EGP-unit string (no currency symbol, no decimals). */
export function centsToWholeUnits(cents) {
  const n = Number(cents);
  return formatWholeNumber((Number.isFinite(n) ? n : 0) / 100);
}

/** First non-space character of a name, for a leaderboard avatar initial. Null-safe. */
export function initials(name) {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed ? trimmed[0] : '—';
}

const DATE_LOCALE = {
  ar: 'ar-u-nu-latn',
  en: 'en-u-nu-latn',
};

/**
 * "1–14 يوليو" (same month) or "1 يوليو – 14 أغسطس" (spanning months).
 * Returns '' when either date is missing/invalid — callers render nothing.
 */
export function formatDealDateRange(startAt, endAt, locale = 'ar') {
  if (!startAt || !endAt) return '';
  const start = new Date(startAt);
  const end = new Date(endAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';

  const intlLocale = DATE_LOCALE[locale] ?? DATE_LOCALE.ar;
  const dayFmt = new Intl.DateTimeFormat(intlLocale, { day: 'numeric' });
  const monthFmt = new Intl.DateTimeFormat(intlLocale, { month: 'long' });
  const startDay = dayFmt.format(start);
  const endDay = dayFmt.format(end);
  const startMonth = monthFmt.format(start);
  const endMonth = monthFmt.format(end);

  if (startMonth === endMonth) {
    return `${startDay}–${endDay} ${endMonth}`;
  }
  return `${startDay} ${startMonth} – ${endDay} ${endMonth}`;
}
